import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function PhotoSlidesManager() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    imageUrl: '',
    caption: '',
    order: 1,
  });

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      const q = query(collection(db, 'photoSlides'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const slidesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSlides(slidesData);
    } catch (error) {
      console.error('Error loading photo slides:', error);
      alert('Failed to load photo slides');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSlide) {
        await updateDoc(doc(db, 'photoSlides', editingSlide.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Photo slide updated successfully!');
      } else {
        await addDoc(collection(db, 'photoSlides'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Photo slide added successfully!');
      }
      setShowModal(false);
      setEditingSlide(null);
      resetForm();
      loadSlides();
    } catch (error) {
      console.error('Error saving photo slide:', error);
      alert('Failed to save photo slide');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      imageUrl: slide.imageUrl,
      caption: slide.caption,
      order: slide.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo slide?')) return;

    try {
      await deleteDoc(doc(db, 'photoSlides', id));
      alert('Photo slide deleted successfully!');
      loadSlides();
    } catch (error) {
      console.error('Error deleting photo slide:', error);
      alert('Failed to delete photo slide');
    }
  };

  const resetForm = () => {
    setFormData({
      imageUrl: '',
      caption: '',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Photo Gallery Management</h2>
            <button
              onClick={() => {
                setEditingSlide(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Photo
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && slides.length === 0 && (
            <p>No photo slides found. Add your first photo!</p>
          )}

          {!loading && slides.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Image Preview</th>
                  <th>Image URL</th>
                  <th>Caption</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.map((slide) => (
                  <tr key={slide.id}>
                    <td>{slide.order}</td>
                    <td>
                      <img
                        src={slide.imageUrl}
                        alt={slide.caption}
                        style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/80x60?text=No+Image'; }}
                      />
                    </td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {slide.imageUrl}
                    </td>
                    <td>{slide.caption}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(slide)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(slide.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="info-box">
          <h3>Image URL Guidelines</h3>
          <p>• Use direct image URLs from reliable sources (e.g., Unsplash, your own hosting)</p>
          <p>• Recommended size: 800x600px or higher</p>
          <p>• Supported formats: JPG, PNG, WebP</p>
          <p>• Example sources:</p>
          <ul>
            <li><a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a> - Free high-quality images</li>
            <li><a href="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800" target="_blank" rel="noopener noreferrer">Example URL format</a></li>
          </ul>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingSlide ? 'Edit Photo Slide' : 'Add New Photo Slide'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Image URL *</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://images.unsplash.com/photo-..."
                  required
                />
                {formData.imageUrl && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      style={{ width: '100%', maxWidth: '400px', borderRadius: '8px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Caption</label>
                <input
                  type="text"
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  placeholder="Optional caption for the image"
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingSlide ? 'Update Photo' : 'Add Photo'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSlide(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoSlidesManager;
